import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, Sparkles, Heart, Check, AlertTriangle, Lock,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSEO } from "@/lib/seo";
import { isIOSAppBuild } from "@/lib/platform";

/**
 * Pricing — THE CANONICAL LADDER (CANON block 2; quoted, never paraphrased).
 *
 *   $12/year  Starter — Graduated Boards (6→12→18 words)
 *   $7/month  Membership — the complete manual product
 *   $70/year  Membership annual — pay for 10 months, get 2 free (R2-I)
 *
 * Checkout runs a REAL Stripe flow. While the payment account is in test
 * mode, every surface says so plainly (immutable rule 13: honest gating,
 * never a fake success pretending money moved). Schools & therapists are
 * founder-controlled distribution — no checkout here (R4-C/R4-G). Hardship
 * is founder discretion, never a tier (Part L). No trials, no promo credits.
 */

const LADDER = [
  {
    id: "starter_yearly",
    name: "Starter — Graduated Boards",
    price: "$12",
    per: "/year",
    anchor: "3¢ a day",
    Icon: Heart,
    blurb:
      "For babies, toddlers, and small kids before their first real board: " +
      "premade boards that graduate 6→12→18 words, with license-safe " +
      "pictures and a clear generic voice. Layouts are fixed — and you add " +
      "your OWN voice recordings and your own pictures to the tiles. Basic " +
      "communication with the family's soul in it.",
    points: [
      "Graduating boards: 6 → 12 → 18 words — each step keeps what your child knows",
      "Your voice and your photos on the tiles",
      "Works offline on the child's tablet",
      "Outgrowing 18 words is the Membership moment — upgrading is one lossless step",
    ],
  },
  {
    id: "membership_monthly",
    name: "Membership",
    price: "$7",
    per: "/month",
    anchor: "23¢ a day",
    Icon: ShieldCheck,
    featured: true,
    blurb:
      "The complete product: unlimited boards you build by hand, the child's " +
      "own Board mode, progress dashboard, and professional connections. " +
      "No credits are ever needed for any of it.",
    points: [
      "Unlimited manual boards — build and edit by hand, always included",
      "Board Only mode for the child's device — no AI near the child, by architecture",
      "Progress dashboard, seat files, prep intake",
      "Professional connections — invite the therapist you know and trust",
      "Below the common $9–$9.99 AAC subscription class (checked July 2026)",
    ],
  },
  {
    id: "membership_yearly",
    name: "Membership — Annual",
    price: "$70",
    per: "/year",
    anchor: "pay for 10 months, get 2 free",
    Icon: Sparkles,
    blurb:
      "Everything in Membership, billed once a year. An eligible installment " +
      "option appears at checkout when available — a one-time purchase, " +
      "never an auto-renewing installment plan.",
    points: [
      "Everything in Membership",
      "Two months on us against the monthly price",
      "Installments (when eligible) shown right at checkout",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";
  const iosBuild = isIOSAppBuild();

  const [testMode, setTestMode] = useState(null);
  // Founder ruling 2026-07-11: checkout is Coming Soon until the EIN lands.
  // Server-controlled (CHECKOUT_ENABLED env) so the flip is config, not code.
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  useSEO({
    title: "Pricing — SonSpeak",
    description:
      "Starter Graduated Boards $12/year. Membership $7/month or $70/year. " +
      "Credits pay for AI work. Private by physics.",
    path: "/pricing",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/packages");
        if (alive) {
          setTestMode(Boolean(data.test_mode));
          setCheckoutEnabled(Boolean(data.checkout_enabled));
        }
      } catch {
        if (alive) setTestMode(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  const buy = async (packageId) => {
    setError("");
    setBusyId(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusyId("");
    }
  };

  return (
    <div className="min-h-screen dotted-bg bg-[color:var(--cream)]" data-testid="pricing-page">
      {/* No local header here — the global site header already provides nav +
          the Sign up CTA. (Removed the redundant Home / Create account row.) */}
      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-12">
        <h1 className="display text-3xl sm:text-4xl font-extrabold mb-2">
          Simple pricing, priced for families
        </h1>
        <p className="text-[color:var(--ink-soft)] max-w-2xl leading-relaxed mb-2">
          I kept this affordable on purpose. A printed board can't grow with
          your child, AAC apps commonly run $200–$300, and an insurance device
          can take years. SonSpeak works on the tablet you already have — and
          grows with your child, in your voice, with your photos.
        </p>
        <p className="display font-bold text-sm mb-8" data-testid="pricing-split-line">
          Your board and its tools are yours. Credits only pay for the optional
          AI helpers — never for your child&apos;s voice.
        </p>

        {!checkoutEnabled && !iosBuild && (
          <div className="brutal-card bg-[color:var(--gold)] p-4 mb-6 flex items-start gap-3" data-testid="pricing-coming-soon-banner">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <div className="display font-extrabold mb-0.5">Checkout — coming soon</div>
              These are the real prices, posted early so there are no surprises.
              Payments open once my business registration completes. Until then,
              early access continues with no payment — and I'll never show a
              checkout that pretends money moved.
            </div>
          </div>
        )}

        {checkoutEnabled && testMode === true && !iosBuild && (
          <div className="brutal-card bg-[color:var(--gold)] p-4 mb-6 flex items-start gap-3" data-testid="pricing-test-mode-banner">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <div className="display font-extrabold mb-0.5">Beta checkout — test mode</div>
              Payments run through Stripe&apos;s <strong>test mode</strong> while my
              payment account is being finalized. <strong>No real money moves.</strong>{" "}
              You can walk the whole flow with Stripe&apos;s test card
              (4242&nbsp;4242&nbsp;4242&nbsp;4242). I&apos;ll never show a success
              screen that pretends otherwise.
            </div>
          </div>
        )}

        {cancelled && (
          <div className="brutal-card bg-white p-4 mb-6 text-sm" data-testid="pricing-cancelled">
            Checkout cancelled — nothing was charged.
          </div>
        )}

        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-4 mb-6 text-sm" data-testid="pricing-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {LADDER.map((tier) => {
            const Icon = tier.Icon;
            return (
              <section
                key={tier.id}
                className={`brutal-card p-6 flex flex-col ${tier.featured ? "bg-[color:var(--paper)]" : "bg-white"}`}
                data-testid={`pricing-card-${tier.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-10 h-10 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] flex items-center justify-center">
                    <Icon size={18} />
                  </span>
                  <h2 className="display text-lg font-extrabold leading-tight">{tier.name}</h2>
                </div>
                <div className="mb-1">
                  <span className="display text-4xl font-extrabold">{tier.price}</span>
                  <span className="text-[color:var(--ink-soft)]">{tier.per}</span>
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--coral-deep)] mb-3">
                  {tier.anchor}
                </div>
                <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed mb-4">{tier.blurb}</p>
                <ul className="space-y-2 mb-5 flex-1">
                  {tier.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm">
                      <Check size={14} className="shrink-0 mt-0.5 text-[color:var(--coral-deep)]" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                {iosBuild ? (
                  <p className="text-xs text-[color:var(--ink-soft)] text-center">
                    Plans are managed on sonspeak.com.
                  </p>
                ) : !checkoutEnabled ? (
                  <div
                    className="pill-btn w-full justify-center opacity-70 cursor-default select-none"
                    aria-disabled="true"
                    data-testid={`pricing-coming-soon-${tier.id}`}
                  >
                    Checkout coming soon
                  </div>
                ) : user ? (
                  <button
                    className="pill-btn primary w-full justify-center"
                    onClick={() => buy(tier.id)}
                    disabled={Boolean(busyId)}
                    data-testid={`pricing-buy-${tier.id}`}
                  >
                    {busyId === tier.id ? "Opening checkout…" : "Choose this plan"}
                  </button>
                ) : (
                  <Link
                    to="/register"
                    className="pill-btn primary w-full justify-center"
                    data-testid={`pricing-signup-${tier.id}`}
                  >
                    Create account to choose
                  </Link>
                )}
              </section>
            );
          })}
        </div>

        {!iosBuild && checkoutEnabled && (
          <p className="text-center text-sm text-[color:var(--ink-soft)] mt-6" data-testid="pricing-checkout-line">
            Choose $7 monthly, save with $70 annual, or use an eligible
            installment option shown at checkout.
          </p>
        )}
        {!iosBuild && !checkoutEnabled && (
          <p className="text-center text-sm text-[color:var(--ink-soft)] mt-6" data-testid="pricing-checkout-line">
            When checkout opens: $7 monthly, save with $70 annual, or an
            eligible installment option shown at checkout.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <section className="brutal-card bg-white p-6" data-testid="pricing-credits-card">
            <h3 className="display text-lg font-extrabold mb-2 flex items-center gap-2">
              <Sparkles size={16} /> Credits — the AI&apos;s time
            </h3>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              Every AI action — instant board drafts, guided custom boards,
              outside helpers — is paid work with a posted credit price shown
              before it runs. Fixed prices for fixed jobs, a live balance for
              open-ended work, never a surprise. Manual boards never touch
              credits: membership is complete on its own. During early access,
              I turn on credits for you by hand while checkout finishes.
            </p>
          </section>

          <section className="brutal-card bg-white p-6" data-testid="pricing-schools-card">
            <h3 className="display text-lg font-extrabold mb-2 flex items-center gap-2">
              <Heart size={16} /> Schools &amp; therapists
            </h3>
            <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
              I work with schools and clinicians directly — there&apos;s no
              checkout for professionals. Pairing with a family always needs
              the parent&apos;s permission, and the parent approves every
              suggested change. If you work with kids who need a voice,{" "}
              <Link to="/contact" className="underline">reach out</Link>.
            </p>
          </section>
        </div>

        <div className="brutal-card bg-[color:var(--paper)] p-6 mt-5" data-testid="pricing-lapse-card">
          <h3 className="display text-lg font-extrabold mb-2 flex items-center gap-2">
            <Lock size={16} /> If a plan ever lapses
          </h3>
          <p className="text-sm text-[color:var(--ink-soft)] leading-relaxed">
            You get 14 days of grace with clear warnings on the caregiver side —
            and only there: nothing payment-shaped ever appears on a child&apos;s
            screen. Your boards, photos, and recordings are never deleted and
            never held hostage — viewing, exporting, and billing controls stay
            available, always. Reactivating restores everything instantly,
            because your family&apos;s data never left your device.
          </p>
        </div>
      </main>
    </div>
  );
}
